<template>
  <div class="report-box" v-loader.opacity="loading">
    <div class="alert" :class="alert.class" role="alert" v-if="alert.message">
      <span v-html="alert.message"></span>
      <button type="button" class="btn-close" aria-label="Close" @click="alert.message = null"></button>
    </div>

    <div class="alert alert-light alert-dismissible fade show" v-if="isPaidService && showExperienceAlert">
      <IconLamp/>

      <p v-if="hasUnifiedReportFeature">{{ $t('report.newExperienceInfo') }}</p>
      <p v-else>{{ $t('report.newExperienceAvaiable') }}</p>

      <div class="">
        <button class="btn btn-outline-secondary" type="button" name="button" @click="updateUnifiedReportFeature(hasUnifiedReportFeature ? false : true)">
          {{ $t('report.oldExperience') }}
        </button>

        <button class="btn btn-outline-secondary" type="button" name="button" data-bs-dismiss="alert" aria-label="Close" @click="showExperienceAlert = false">
          <IconCross/>
          {{ $t('report.close') }}
        </button>
      </div>
    </div>

    <div class="diagnosis-box" :class="{'has-experience-alert': isPaidService && showExperienceAlert}">
      <div class="content-diagnosis">
        <h2>
          <span>1</span>
          <span>2</span>

          {{ $t('report.reportInformation') }}
        </h2>

        <div v-if="hasUnifiedReportFeature && isPaidService && isLoaded" class="form-floating hedis-check">
          <div class="form-check form-switch">
            <label class="form-check-label" for="isHedisUnifiedReport">
              {{ $t('exam.hedisPattern') }}
              <span>{{ $t('exam.activateHedisPattern') }}</span>
            </label>
            <input class="form-check-input"
                   type="checkbox"
                   role="switch"
                   id="isHedisUnifiedReport"
                   v-model="isHedisUnifiedReport"
                   @click="clearUnifiedHedis"
                   data-test="checkboxHedisUnifiedReport">
          </div>
        </div>

        <div v-if="(tabActive === 'hedisReport' || isHedisUnifiedReport) && patientReportInfo">
          <h3>{{ $t('user.exam') }}</h3>

          <div class="second-header-hedis">
            <div>
              <span>{{ $t('report.patientName') }}</span>
              <p>
                {{ patientReportInfo.patientFullName }}
                <BaseFfsBadge v-if="patientReportInfo.patientFfs"/>
              </p>
            </div>
            <div>
              <span>{{ $t('report.dateOfBirth') }}</span>
              <p>{{ patientReportInfo.dateOfBirth }}</p>
            </div>
            <div>
              <span>{{ $t('report.DateOfScreening') }}</span>
              <p>{{ patientReportInfo.dateOfScreening }}</p>
            </div>
          </div>
        </div>

        <form class="scrollbar"
              id="formCreateDiagnosis"
              :class="{'hedis-form-adjustment': tabActive === 'hedisReport' && !hasUnifiedReportFeature,
                       'unified-report': hasUnifiedReportFeature && isLoaded,
                       'hedis-unified-report': hasUnifiedReportFeature && isPaidService && (isHedisUnifiedReport || currentReport?.isHedisReport)}">

          <div v-if="hasUnifiedReportFeature && isPaidService && isLoaded">
            <ReportUnified
              v-if="!isHedisUnifiedReport"
              ref="reportUnified"
              :template-options="templateOptions"
              :column-options="columnOptions"
              :current-report="currentReport"
              :selected-images="selectedImages"
              :exam-images="examImages"
              :exam-id="exam_id"
              :unified-exam-type="unifiedExamType"
              :report-status="reportStatus"
              @reportGenerated="unifiedReportSaved"/>

            <ReportHedis
              v-else
              :is-unified-report="hasUnifiedReportFeature"
              :initialData="hedisReportData"
              @update:hedis-data="setHedisReportData"/>

            <ReportStatusSelection
              v-model="reportStatus"
              :options="reportStatusOptions"
              :is-unified-report="true"
              :invalid="showReportStatusError && !reportStatus"/>
          </div>

          <div v-else>
            <div v-if="tabActive !== 'hedisReport'">
              <h3 class="mb-0">{{ $t('report.captures') }}</h3>
            </div>

            <div v-if="tabActive !== 'hedisReport'" class="form-floating v-select-floating">
              <v-select
                class="v-select form"
                :class="{'disabled': selectedTemplate != null}"
                id="selectColumns"
                v-model="currentReport.columns"
                :reduce="x => x.value"
                :options="columnOptions"
                :placeholder="$t('report.columnNumber')"
                :disabled="selectedTemplate != null"
                data-test="vSelectColumns">
              </v-select>
              <label for="selectColumns">{{ $t('report.columnNumber') }}</label>
            </div>

            <div v-if="tabActive !== 'hedisReport'" class="form-floating">
              <div class="form-check form-switch">
                <label class="form-check-label" for="fullWidthPanoramic">{{ $t('exam.panoProcessOnReport') }}</label>
                <input class="form-check-input" type="checkbox" role="switch" id="fullWidthPanoramic" v-model="currentReport.fullWidthPanoramic" data-test="checkboxFullWidthPanoramic">
              </div>
            </div>

            <div>
              <h3>{{ $t('report.model') }}</h3>
              <p>{{ $t('report.modelChoose') }}</p>
            </div>

            <ul class="nav nav-tabs report-nav-tabs">
              <li v-if="hasHedisReportFeature" class="nav-item" role="presentation">
                <button class="nav-link" :class="{'active': tabActive == 'hedisReport'}" id="hedisReport-tab" data-bs-toggle="tab" data-bs-target="#hedisReport"
                        type="button" role="tab" aria-controls="hedisReport" aria-selected="false" @click="switchReportTab('hedisReport')">{{ $t('report.hedis') }}</button>
              </li>
              <li class="nav-item" role="presentation">
                <button class="nav-link" :class="{'active': tabActive == 'quickReport'}" id="quickReport-tab" data-bs-toggle="tab" data-bs-target="#quickReport"
                        type="button" role="tab" aria-controls="quickReport" aria-selected="false" @click="switchReportTab('quickReport')">{{ $t('report.simplified') }}</button>
              </li>
              <li class="nav-item" role="presentation">
                <button class="nav-link" :class="{'active': tabActive == 'completeReport'}" id="completeReport-tab"
                        data-bs-toggle="tab" data-bs-target="#completeReport"
                        type="button" role="tab" aria-controls="completeReport" aria-selected="false" @click="switchReportTab('completeReport')">{{ $t('report.complete') }}</button>
              </li>
            </ul>

            <div>
              <h3>{{ $t('report.diagnosis') }}</h3>
              <p v-if="tabActive == 'quickReport'">{{ $t('report.fillEachEye') }}</p>
            </div>

            <div class="tab-content" id="myTabContent">
              <div class="tab-pane fade" :class="{'show active': tabActive == 'quickReport'}" id="quickReport" role="tabpanel" aria-labelledby="profile-tab">
                <ReportQuick v-if="isLoaded"
                  :exam="exam"
                  :observation="quickReportObservation"
                  :loaded_entries="loadedDiagnosisEntries"
                  @entries-changed="setQuickReportData">
                </ReportQuick>
              </div>

              <div class="tab-pane fade" :class="{'show active': tabActive == 'hedisReport'}" id="hedisReport" role="tabpanel" aria-labelledby="hedis-tab">
                <ReportHedis v-if="isLoaded && tabActive == 'hedisReport'"
                  :initialData="hedisReportData"
                  @update:hedis-data="setHedisReportData">
                </ReportHedis>
              </div>

              <div class="tab-pane fade" :class="{'show active': tabActive == 'completeReport'}" id="completeReport" role="tabpanel" aria-labelledby="home-tab">
                <div class="form-floating">
                  <select id="diagnosis_select" class="js-example-basic-multiple js-states form-control" name="diagnosis_select" multiple="multiple"
                      style="width:100%;" :placeholder="$t('exam.diagnosisCID10')" data-test="selectDiagnosis"></select>
                  <label for="diagnosis_select">{{ $t('exam.diagnosisCID10') }}</label>

                  <div class="selected-diagnosis-box" id="selectedDiagnosis" v-if="selectedDiagnosis.length">
                    <span v-for="item in selectedDiagnosis" :key="item.id" class="tag2 alert" role="tag"
                          :data-value="item.id">
                      <span>{{ item.text }}</span>
                      <a href="#" @click="removeDiagnosisEntry(item.id)">
                        <IconCross/>
                      </a>
                    </span>
                  </div>
                </div>

                <div class="form-floating v-select-floating">
                  <v-select
                    class="v-select form"
                    id="selectTemplate"
                    v-model="selectedTemplate"
                    label="name"
                    :reduce="x => x.id"
                    :options="templateOptions"
                    @option:selected="switchTemplate"
                    :placeholder="'Template'"
                    data-test="vSelectTemplate">
                  </v-select>
                  <label for="selectTemplate">Template</label>
                </div>

                <ReportForm v-if="isLoaded && tabActive == 'completeReport'" ref="reportForm"
                  :content="customTemplate"
                  @custom-template="setCustomTemplate">
                </ReportForm>
              </div>
            </div>

            <div class="form-floating">
              <textarea class="form-control" id="observation" name="name" rows="3" v-model="currentReport.observation"
                        :placeholder="$t('exam.observations')" data-test="textareaObservations"></textarea>
              <label for="observation">{{ $t('exam.observations') }}</label>
            </div>

            <ReportStatusSelection
              v-model="reportStatus"
              :options="reportStatusOptions"
              :invalid="showReportStatusError && !reportStatus"/>
          </div>
        </form>
      </div>

      <div class="content-images">
        <h2>
          <span>2</span>
          <span>1</span>

          {{ $t('report.selectCaptures') }}
        </h2>

        <div class="flex-start">
          <button class="btn btn-outline-secondary" type="button" name="button" @click="checkAll">{{ $t('exam.all') }}</button>

          <button class="btn btn-outline-secondary" type="button" name="button" @click="uncheckAll">{{ $t('exam.none') }}</button>
        </div>

        <div class="alert" :class="alertImages.class" role="alert" v-if="alertImages.message">
          <span v-html="alertImages.message"></span>
          <button type="button" class="btn-close" aria-label="Close" @click="alertImages.message = null"></button>
        </div>

        <div class="diagnosis-gallery scrollbar" v-else>
          <lightgallery-vue class="ease-all grid-wrap-between" ref="lGalleryDiagnosis" id="lGalleryDiagnosis" :settings="lGalleryDiagnosisSettings" :onInit="onInit" :onAfterOpen="extraCustomization">
             <template v-for="(item, index) in examImages" :key="index">
               <a class="thumbnail-box loading-eye" :class="item.type" onclick="return false" :href="dataPath +'/'+ item.name" :data-test="`anchorImage_${index}`">
                 <img class="lg-selector ease-all" alt="" :class="{ 'eyer2-image': item.eyerVersion > 0 }"
                      :src="thumbDataPath +'/'+ item.name" @load="removeLoading($event)" @error="$utils.enableDownloadOnError($event, item, doDownload)"/>
                 <div class="checkbox">
                   <BaseRoundedCheckBox :id="item.uuid" :item="item" @checked="doCheck"/>
                 </div>
               </a>
             </template>
          </lightgallery-vue>
        </div>
      </div>
    </div>

    <div class="action-bar">
      <button class="btn btn-success"
              type="button"
              name="button"
              data-test="btnSave"
              v-if="canSaveReport"
              @click="doCreate()">
        {{ $t(currentReport.id ? 'updateAction' : 'create') }}
      </button>
      <!-- @click="hasUnifiedReportFeature && !isHedisUnifiedReport ? $refs.reportUnified.doSave() : doCreate()"> -->
      <button class="btn btn-outline-secondary" type="button" name="button" data-test="btnDownloadPdf" v-if="currentReport.id" @click="doDownloadPdf">{{ $t('Download') }}</button>
      <button class="btn btn-secondary" type="button" name="button" data-test="btnBack" @click="doCancel" v-if="report_id">{{ $t('cancel') }}</button>
    </div>

    <teleport to="body">
      <BaseGenericOffcanvas :offcanvas_id="'offcanvasReportGeneric'" ref="offcanvasReportGeneric"/>
    </teleport>
  </div>
</template>

<style src="./report.scss" lang="scss" scoped></style>

<script src="./report.js"></script>
